import { cva } from 'class-variance-authority'

import { cn } from '~/utils/tailwind-utils'

const TERTIARY_CLASSNAME =
  'bg-transparent focus-visible:ring-2 focus-visible:ring-focus ring-inset text-primary hover:bg-surface-strong data-[state=open]:bg-surface-strong disabled:hover:bg-transparent'

const TERTIARY_SHORTCUT_CLASSNAME =
  'bg-gray-200 text-gray-900 group-hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:group-hover:bg-gray-600'

export const buttonVariants = cva(
  'group flex flex-shrink relative select-none self-start items-center whitespace-nowrap font-medium [&>svg]:size-4 [&>svg]:text-inherit focus-visible:outline-none transition-[transform,background-color,color,opacity,box-shadow] duration-150 ease-out active:scale-[0.98]',
  {
    variants: {
      variant: {
        primary:
          'focus-visible:shadow-focus-bordered-light dark:focus-visible:shadow-focus-bordered-dark dark:bg-white dark:text-black bg-gray-900 text-white active:brightness-[.85] hover:brightness-95 hover:bg-gray-800 dark:hover:bg-gray-200 disabled:hover:bg-gray-900 dark:disabled:hover:bg-white',
        secondary:
          'bg-surface-raised ring-1 focus-visible:ring-2 focus-visible:ring-focus ring-inset ring-default text-primary active:brightness-[.95] hover:bg-surface-muted disabled:hover:bg-surface-raised',
        danger:
          'focus-visible:shadow-focus-bordered-light dark:focus-visible:shadow-focus-bordered-dark bg-red-500 text-white active:brightness-[.9] hover:brightness-95 disabled:hover:bg-red-500',
        tertiary: cn(TERTIARY_CLASSNAME, 'active:brightness-[.9]'),
        // tertiary at normal weight, without the press-darken
        nav: cn(TERTIARY_CLASSNAME, 'font-normal'),
        secondaryAction:
          'focus-visible:ring-2 focus-visible:ring-focus text-blue-500 bg-transparent !text-sm !border-none !p-0 hover:opacity-75 active:opacity-60 [&>svg]:size-3 !gap-1.5',
      },
      size: {
        small: 'text-sm gap-2 px-2.5 py-1.5 [&>svg]:my-0.5',
        base: 'text-base gap-2.5 px-3.5 py-2.5 [&>svg]:my-1',
      },
      shape: {
        rounded: 'rounded-lg',
        pill: 'rounded-full',
      },
      isSelected: {
        true: '',
      },
      isDisabled: {
        true: 'opacity-50 cursor-not-allowed active:scale-100 active:brightness-100 active:opacity-50 hover:brightness-100 hover:opacity-50 hover:bg-inherit dark:hover:bg-inherit hover:border-inherit',
      },
      isLoading: {
        true: 'cursor-default active:scale-100 active:brightness-100 active:opacity-50 hover:brightness-100 hover:opacity-50 hover:bg-inherit hover:border-inherit',
      },
      fullWidth: {
        true: 'w-full',
      },
      alignLeft: {
        true: 'justify-start',
        false: 'justify-center',
      },
    },
    compoundVariants: [
      {
        variant: ['tertiary', 'nav'],
        isSelected: true,
        class: 'bg-surface-strong',
      },
      {
        variant: 'secondary',
        isSelected: true,
        class: 'bg-surface-muted',
      },
      // secondaryAction stays fully opaque while loading — the icon swap alone signals progress, dimming the subtle blue text would hurt legibility
      {
        variant: 'secondaryAction',
        isLoading: true,
        class: '!opacity-100 hover:!opacity-100 active:!opacity-100',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'base',
      alignLeft: false,
      shape: 'rounded',
    },
  },
)

export const buttonShortcutVariants = cva(
  'font-normal -mr-1 py-0.5 flex whitespace-nowrap items-center justify-center rounded-[4px] transition-colors duration-100 ease-out',
  {
    variants: {
      variant: {
        primary:
          'bg-gray-600 text-white group-hover:bg-gray-600 dark:bg-gray-200 dark:text-black dark:group-hover:bg-gray-300',
        secondary:
          'bg-surface-strong text-primary group-hover:bg-gray-300 dark:group-hover:bg-gray-700',
        tertiary: TERTIARY_SHORTCUT_CLASSNAME,
        nav: TERTIARY_SHORTCUT_CLASSNAME,
        danger: 'bg-red-400 text-white',
        secondaryAction: 'bg-blue-500 text-white',
      },
      fullWidth: {
        true: 'absolute right-2.5 mr-0',
      },
      size: {
        small: 'px-1 text-xs',
        base: 'px-1.5 text-sm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'base',
    },
  },
)
